import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Single-media PDP lightbox (OHP001P-133). Markup: snippets/bt-pdp-gallery.liquid.
 *
 * Tiles carry `[data-bt-pdp-zoom="image"]` (`data-src`, `data-alt`) or
 * `[data-bt-pdp-zoom="video"]` with a sibling `<template data-bt-pdp-zoom-media>`.
 * Images: click / tap zooms in at that point, drag to pan, click again to zoom out,
 * two-finger pinch, keyboard +/-. Clicking outside the image closes. Videos: native controls.
 * Prev / next walks the tiles of the clicked row (swipe when not zoomed, arrows, arrow keys).
 */
const MIN_SCALE = 1;
const CLICK_SCALE = 2.5;
const MAX_SCALE = 3;
const SWIPE_DISTANCE = 50;

class BtPdpZoom extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {HTMLElement[]} */
  #items = [];

  #index = 0;

  /** Opened with Enter / Space — only then hand focus back to the tile on close. */
  #openedByKeyboard = false;

  /** @type {HTMLImageElement | null} */
  #image = null;

  #scale = MIN_SCALE;
  #x = 0;
  #y = 0;

  /** @type {Map<number, { x: number, y: number }>} */
  #pointers = new Map();

  /** @type {{ x: number, y: number, tx: number, ty: number } | null} */
  #drag = null;

  /** @type {{ distance: number, scale: number } | null} */
  #pinch = null;

  /** @type {{ x: number, y: number } | null} */
  #swipe = null;

  /** A drag or pinch just ended — swallow the trailing click so it doesn't toggle zoom. */
  #gestured = false;

  #wheelTimer = 0;

  /** Full-size URLs already requested on hover / press. */
  /** @type {Set<string>} */
  #prefetched = new Set();

  get #dialog() {
    const dialog = this.querySelector('[data-bt-pdp-zoom-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  get #stage() {
    const stage = this.querySelector('[data-bt-pdp-zoom-stage]');
    return stage instanceof HTMLElement ? stage : null;
  }

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const dialog = this.#dialog;
    const stage = this.#stage;
    if (!dialog || !stage) return;

    const root = this.closest('media-gallery') || this.parentElement;
    root?.addEventListener('click', this.#onGalleryClick, { signal });
    root?.addEventListener('pointerover', this.#prefetch, { signal, passive: true });
    root?.addEventListener('pointerdown', this.#prefetch, { signal, passive: true });

    dialog.addEventListener('click', this.#onDialogClick, { signal });
    dialog.addEventListener('keydown', this.#onKeyDown, { signal });
    dialog.addEventListener('close', this.#onClose, { signal });

    stage.addEventListener('pointerdown', this.#onPointerDown, { signal });
    stage.addEventListener('pointermove', this.#onPointerMove, { signal });
    stage.addEventListener('pointerup', this.#onPointerUp, { signal });
    stage.addEventListener('pointercancel', this.#onPointerUp, { signal });
    stage.addEventListener('wheel', this.#onWheel, { signal, passive: false });
    stage.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    window.clearTimeout(this.#wheelTimer);
    if (this.#dialog?.open) this.#dialog.close();
    unlockScroll(this);
  }

  /** @param {PointerEvent} event */
  #prefetch = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-bt-pdp-zoom="image"]');
    const src = trigger instanceof HTMLElement ? trigger.dataset.src : '';
    if (!src || this.#prefetched.has(src)) return;
    this.#prefetched.add(src);
    const image = new Image();
    image.decoding = 'async';
    image.src = src;
  };

  /** @param {MouseEvent} event */
  #onGalleryClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-bt-pdp-zoom]');
    if (!(trigger instanceof HTMLElement) || this.contains(trigger)) return;

    event.preventDefault();
    this.#openedByKeyboard = event.detail === 0;
    this.#open(trigger);
  };

  /** @param {HTMLElement} trigger */
  #open(trigger) {
    const dialog = this.#dialog;
    if (!dialog) return;

    const row = trigger.closest('.bt-pdp-gallery__grid, [data-bt-pdp-gallery-track]');
    this.#items = row
      ? [...row.querySelectorAll('[data-bt-pdp-zoom]')].filter((item) => item instanceof HTMLElement)
      : [trigger];
    this.#index = Math.max(0, this.#items.indexOf(trigger));

    if (!this.#show(trigger)) return;
    dialog.showModal();
    lockScroll(this);
    this.#stage?.querySelector('video')?.play().catch(() => {});
  }

  /** @param {number} delta */
  #step(delta) {
    const next = this.#index + delta;
    const trigger = this.#items[next];
    if (!trigger) return;
    this.#index = next;
    this.#show(trigger);
    this.#stage?.querySelector('video')?.play().catch(() => {});
  }

  #syncNav() {
    const dialog = this.#dialog;
    if (!dialog) return;
    const ends = { '-1': this.#index <= 0, 1: this.#index >= this.#items.length - 1 };
    for (const button of dialog.querySelectorAll('[data-bt-pdp-zoom-nav]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const hidden = ends[/** @type {'-1' | '1'} */ (button.dataset.btPdpZoomNav)] ?? true;
      if (hidden && document.activeElement === button) this.#stage?.focus({ preventScroll: true });
      button.hidden = hidden;
    }
  }

  /**
   * Render one tile into the stage.
   * @param {HTMLElement} trigger
   * @returns {boolean}
   */
  #show(trigger) {
    const dialog = this.#dialog;
    const stage = this.#stage;
    if (!dialog || !stage) return false;

    stage.replaceChildren();
    stage.removeAttribute('data-outside');
    this.#image = null;
    this.#zoomOut();

    const type = trigger.dataset.btPdpZoom;
    if (type === 'image') {
      const src = trigger.dataset.src;
      if (!src) return false;
      const tileImage = trigger.querySelector('img');
      const preview = tileImage instanceof HTMLImageElement && tileImage.complete ? tileImage.currentSrc : '';

      const image = new Image();
      image.className = 'bt-pdp-zoom__image';
      image.alt = trigger.dataset.alt || '';
      image.decoding = 'async';
      image.draggable = false;
      image.src = preview || src;
      stage.append(image);
      this.#image = image;

      if (preview && preview !== src) {
        const full = new Image();
        full.src = src;
        full
          .decode()
          .then(() => {
            if (this.#image === image) image.src = src;
          })
          .catch(() => {});
      }
    } else if (type === 'video') {
      const template = trigger.parentElement?.querySelector('template[data-bt-pdp-zoom-media]');
      if (!(template instanceof HTMLTemplateElement)) return false;
      stage.append(template.content.cloneNode(true));
      this.#pauseGalleryVideos();
    } else {
      return false;
    }

    dialog.dataset.type = type;
    this.#trigger = trigger;
    this.#syncNav();
    return true;
  }

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-bt-pdp-zoom-close]')) return this.#dialog?.close();
    const nav = target.closest('[data-bt-pdp-zoom-nav]');
    if (nav instanceof HTMLElement) return this.#step(Number(nav.dataset.btPdpZoomNav));
    if (this.#gestured) {
      this.#gestured = false;
      return;
    }

    if (this.#image) {
      if (target !== this.#stage && target !== this.#image) return;
      if (!this.#isOverImage(event.clientX, event.clientY)) return this.#dialog?.close();
      if (this.#scale > MIN_SCALE) this.#zoomOut();
      else this.#zoomAt(event.clientX, event.clientY);
      this.#syncOutside(event.clientX, event.clientY);
      return;
    }

    if (target === this.#dialog || target === this.#stage) this.#dialog?.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && !(event.target instanceof HTMLMediaElement)) {
      event.preventDefault();
      this.#step(event.key === 'ArrowLeft' ? -1 : 1);
      return;
    }
    if (!this.#image) return;
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      const rect = this.#stage?.getBoundingClientRect();
      if (rect) this.#zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2);
    } else if (event.key === '-' || event.key === '0') {
      event.preventDefault();
      this.#zoomOut();
    }
  };

  #onClose = () => {
    this.#stage?.replaceChildren();
    this.#image = null;
    this.#pointers.clear();
    window.clearTimeout(this.#wheelTimer);
    this.#zoomOut();
    if (this.#stage) delete this.#stage.dataset.dragging;
    unlockScroll(this);
    this.#items = [];
    if (this.#openedByKeyboard) {
      this.#trigger?.focus({ preventScroll: true });
    } else if (document.activeElement === this.#trigger) {
      this.#trigger?.blur();
    }
    this.#trigger = null;
  };

  #pauseGalleryVideos() {
    const root = this.closest('media-gallery');
    for (const host of root?.querySelectorAll('bt-video-toggle') || []) {
      if (typeof host.pause === 'function') host.pause();
    }
  }

  /**
   * Zoom to CLICK_SCALE keeping the point under the cursor in place.
   * @param {number} clientX
   * @param {number} clientY
   */
  #zoomAt(clientX, clientY) {
    const stage = this.#stage;
    if (!this.#image || !stage) return;
    const rect = stage.getBoundingClientRect();
    const px = clientX - (rect.left + rect.width / 2);
    const py = clientY - (rect.top + rect.height / 2);
    this.#scale = CLICK_SCALE;
    this.#x = px * (1 - CLICK_SCALE);
    this.#y = py * (1 - CLICK_SCALE);
    this.#sync();
  }

  #zoomOut() {
    this.#reset();
    this.#sync();
  }

  #reset() {
    this.#scale = MIN_SCALE;
    this.#x = 0;
    this.#y = 0;
    this.#drag = null;
    this.#pinch = null;
    this.#swipe = null;
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (!this.#image || event.button > 0) return;
    const stage = this.#stage;
    if (!stage) return;

    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.#pointers.size === 1) this.#gestured = false;

    if (this.#pointers.size === 2) {
      this.#drag = null;
      this.#swipe = null;
      this.#gestured = true;
      this.#pinch = { distance: this.#pointerDistance(), scale: this.#scale };
      for (const id of this.#pointers.keys()) this.#capture(id);
      stage.dataset.dragging = 'true';
    } else if (this.#pointers.size === 1 && this.#scale > MIN_SCALE) {
      this.#drag = { x: event.clientX, y: event.clientY, tx: this.#x, ty: this.#y };
      this.#capture(event.pointerId);
      stage.dataset.dragging = 'true';
    } else if (this.#pointers.size === 1 && this.#items.length > 1 && event.pointerType !== 'mouse') {
      this.#swipe = { x: event.clientX, y: event.clientY };
      this.#capture(event.pointerId);
      stage.dataset.dragging = 'true';
    }
  };

  /** @param {number} pointerId */
  #capture(pointerId) {
    try {
      this.#stage?.setPointerCapture(pointerId);
    } catch {
      // Pointer already released.
    }
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerType === 'mouse' && !this.#drag) this.#syncOutside(event.clientX, event.clientY);
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.#pinch && this.#pointers.size === 2) {
      const distance = this.#pointerDistance();
      if (!this.#pinch.distance) return;
      const next = clamp((distance / this.#pinch.distance) * this.#pinch.scale, MIN_SCALE, MAX_SCALE);
      const ratio = next / this.#scale;
      const mid = this.#pinchMidpoint();
      this.#scale = next;
      this.#x = mid.x - (mid.x - this.#x) * ratio;
      this.#y = mid.y - (mid.y - this.#y) * ratio;
      this.#sync();
      return;
    }

    if (this.#drag) {
      if (Math.hypot(event.clientX - this.#drag.x, event.clientY - this.#drag.y) > 4) this.#gestured = true;
      this.#x = this.#drag.tx + (event.clientX - this.#drag.x);
      this.#y = this.#drag.ty + (event.clientY - this.#drag.y);
      this.#sync();
      return;
    }

    if (this.#swipe) {
      const dx = event.clientX - this.#swipe.x;
      if (Math.abs(dx) > 4) this.#gestured = true;
      this.#dialog?.style.setProperty('--bt-pdp-zoom-x', `${dx}px`);
    }
  };

  /**
   * Mouse wheel / trackpad scroll pans the zoomed image.
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    const stage = this.#stage;
    if (!this.#image || !stage || this.#scale <= MIN_SCALE) return;
    event.preventDefault();

    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? stage.clientHeight : 1;
    const dx = event.shiftKey && !event.deltaX ? event.deltaY : event.deltaX;
    const dy = event.shiftKey && !event.deltaX ? 0 : event.deltaY;
    this.#x -= dx * unit;
    this.#y -= dy * unit;

    stage.dataset.dragging = 'true';
    window.clearTimeout(this.#wheelTimer);
    this.#wheelTimer = window.setTimeout(() => {
      if (!this.#drag) delete stage.dataset.dragging;
    }, 120);
    this.#sync();
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    this.#pointers.delete(event.pointerId);
    if (this.#pointers.size < 2) this.#pinch = null;
    this.#drag = null;
    if (this.#stage) delete this.#stage.dataset.dragging;

    const swipe = this.#swipe;
    if (swipe) {
      this.#swipe = null;
      const dx = event.clientX - swipe.x;
      const dy = event.clientY - swipe.y;
      const target = this.#items[this.#index + (dx < 0 ? 1 : -1)];
      if (event.type === 'pointerup' && target && Math.abs(dx) > SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
        this.#step(dx < 0 ? 1 : -1);
      } else {
        this.#sync();
      }
      return;
    }

    const remaining = this.#pointers.values().next().value;
    if (remaining && this.#scale > MIN_SCALE) {
      this.#drag = { x: remaining.x, y: remaining.y, tx: this.#x, ty: this.#y };
      if (this.#stage) this.#stage.dataset.dragging = 'true';
    }
  };

  /** Pinch centre as an offset from the stage centre, so zoom stays under the fingers. */
  #pinchMidpoint() {
    const [a, b] = [...this.#pointers.values()];
    const rect = this.#stage?.getBoundingClientRect();
    if (!a || !b || !rect) return { x: 0, y: 0 };
    return {
      x: (a.x + b.x) / 2 - (rect.left + rect.width / 2),
      y: (a.y + b.y) / 2 - (rect.top + rect.height / 2),
    };
  }

  #pointerDistance() {
    const [a, b] = [...this.#pointers.values()];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  /** Unscaled size of the image as painted by `object-fit: contain`. */
  #fitSize() {
    const image = this.#image;
    const stage = this.#stage;
    if (!image?.naturalWidth || !image.naturalHeight || !stage) return null;
    const ratio = image.naturalWidth / image.naturalHeight;
    const width = Math.min(stage.clientWidth, stage.clientHeight * ratio);
    return { width, height: width / ratio };
  }

  /**
   * @param {number} clientX
   * @param {number} clientY
   */
  #isOverImage(clientX, clientY) {
    const fit = this.#fitSize();
    const rect = this.#stage?.getBoundingClientRect();
    if (!fit || !rect) return true;
    const dx = clientX - (rect.left + rect.width / 2 + this.#x);
    const dy = clientY - (rect.top + rect.height / 2 + this.#y);
    return Math.abs(dx) <= (fit.width * this.#scale) / 2 && Math.abs(dy) <= (fit.height * this.#scale) / 2;
  }

  /**
   * Plain cursor over the empty area around the image, where a click closes.
   * @param {number} clientX
   * @param {number} clientY
   */
  #syncOutside(clientX, clientY) {
    this.#stage?.toggleAttribute('data-outside', Boolean(this.#image) && !this.#isOverImage(clientX, clientY));
  }

  #clampPan() {
    const stage = this.#stage;
    if (!this.#image || !stage || this.#scale <= MIN_SCALE) {
      this.#x = 0;
      this.#y = 0;
      return;
    }

    const fit = this.#fitSize() ?? { width: stage.clientWidth, height: stage.clientHeight };
    const maxX = Math.max(0, (fit.width * this.#scale - stage.clientWidth) / 2);
    const maxY = Math.max(0, (fit.height * this.#scale - stage.clientHeight) / 2);
    this.#x = clamp(this.#x, -maxX, maxX);
    this.#y = clamp(this.#y, -maxY, maxY);
  }

  #sync() {
    this.#clampPan();
    const dialog = this.#dialog;
    if (!dialog) return;

    dialog.style.setProperty('--bt-pdp-zoom-scale', `${this.#scale}`);
    dialog.style.setProperty('--bt-pdp-zoom-x', `${this.#x}px`);
    dialog.style.setProperty('--bt-pdp-zoom-y', `${this.#y}px`);
    dialog.toggleAttribute('data-zoomed', this.#scale > MIN_SCALE);
  }
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

if (!customElements.get('bt-pdp-zoom')) {
  customElements.define('bt-pdp-zoom', BtPdpZoom);
}
